
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';

// Import our new components and schema
import { retentionPolicyFormSchema, RetentionPolicyFormValues, defaultFormValues } from './schemas/retentionPolicySchema';
import { TableNameField } from './fields/TableNameField';
import { StateField } from './fields/StateField';
import { RetentionYearsField } from './fields/RetentionYearsField';
import { DescriptionField } from './fields/DescriptionField';
import { IsActiveField } from './fields/IsActiveField';

interface RetentionPolicyFormProps {
  onSubmit: (values: RetentionPolicyFormValues) => Promise<void>;
  onCancel: () => void;
  defaultValues?: Partial<RetentionPolicyFormValues>;
  isSubmitting?: boolean;
}

export function RetentionPolicyForm({
  onSubmit,
  onCancel,
  defaultValues,
  isSubmitting = false
}: RetentionPolicyFormProps) {
  const form = useForm<RetentionPolicyFormValues>({
    resolver: zodResolver(retentionPolicyFormSchema),
    defaultValues: {
      ...defaultFormValues,
      ...defaultValues
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <TableNameField form={form} />
        <StateField form={form} />
        <RetentionYearsField form={form} />
        <DescriptionField form={form} />
        <IsActiveField form={form} />
        
        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Policy'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

// Re-export the type for usage in other files
export type { RetentionPolicyFormValues } from './schemas/retentionPolicySchema';
